import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosClient from '../api/axiosClient';

const LoginPage = () => {
  const [users, setUsers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [years, setYears] = useState([]);

  const [selectedUser, setSelectedUser] = useState('');
  const [password, setPassword] = useState('');
  const [selectedFirmId, setSelectedFirmId] = useState('');
  const [selectedFid, setSelectedFid] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const fetchConfig = async () => {
    try {
      const res = await axiosClient.get('/auth/config');
      if (res.success) {
        const fetchedUsers = res.data.users || [];
        const fetchedFirms = res.data.firms || [];
        const fetchedYears = res.data.years || [];

        setUsers(fetchedUsers);
        setFirms(fetchedFirms);
        setYears(fetchedYears);

        if (fetchedUsers.length > 0) setSelectedUser(fetchedUsers[0].UserName);
        if (fetchedFirms.length > 0) setSelectedFirmId(fetchedFirms[0].Id);
        if (fetchedYears.length > 0) setSelectedFid(fetchedYears[0].Fid);
      }
    } catch (err) {
      console.error('Failed to load login config:', err);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login({
        username: selectedUser,
        password: password,
        firmId: selectedFirmId,
        fid: selectedFid
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed. Please check credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleUserChange = (uName) => {
    setSelectedUser(uName);
    const found = users.find((u) => u.UserName === uName);
    if (found && found.FirmId) {
      setSelectedFirmId(found.FirmId);
    }
  };

  const currentFirm = firms.find((f) => f.Id === parseInt(selectedFirmId, 10)) || firms[0];
  const firmTitle = currentFirm?.firmName || 'FISH SHOP';

  return (
    <div
      className="d-flex align-items-center justify-content-center vh-100"
      style={{
        background: 'linear-gradient(135deg, #0a2540 0%, #0d3b66 50%, #002244 100%)'
      }}
    >
      <div className="card shadow-lg border-0" style={{ width: '480px', borderRadius: '16px' }}>
        {/* Card Header: Displays Firm Name on header only */}
        <div className="card-header bg-dark text-white text-center py-4 border-0" style={{ borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}>
          <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
            <i className="bi bi-building text-info fs-2"></i>
          </div>
          <h3 className="fw-bold mb-0 text-white tracking-wide text-uppercase">
            {firmTitle}
          </h3>
          {currentFirm?.ContactNo && (
            <p className="text-secondary small mb-0 mt-1">
              <i className="bi bi-telephone me-1"></i> {currentFirm.ContactNo}
            </p>
          )}
        </div>

        <div className="card-body p-4">
          {error && (
            <div className="alert alert-danger d-flex align-items-center gap-2 py-2" role="alert">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Username / Account */}
            <div className="mb-3">
              <label className="form-label fw-semibold text-muted small">SELECT USERNAME</label>
              <div className="input-group">
                <span className="input-group-text bg-light"><i className="bi bi-person text-primary"></i></span>
                <select
                  className="form-select"
                  value={selectedUser}
                  onChange={(e) => handleUserChange(e.target.value)}
                  required
                >
                  {users.map((u) => (
                    <option key={u.id || u.UserName} value={u.UserName}>
                      {u.UserName} {u.role === 'admin' ? '(Admin)' : `(${u.firmName || `Firm #${u.FirmId}`})`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Password */}
            <div className="mb-3">
              <label className="form-label fw-semibold text-muted small">PASSWORD</label>
              <div className="input-group">
                <span className="input-group-text bg-light"><i className="bi bi-lock text-primary"></i></span>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Enter Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Financial Year */}
            <div className="mb-4">
              <label className="form-label fw-semibold text-muted small">FINANCIAL YEAR</label>
              <div className="input-group">
                <span className="input-group-text bg-light"><i className="bi bi-calendar-check text-success"></i></span>
                <select
                  className="form-select"
                  value={selectedFid}
                  onChange={(e) => setSelectedFid(e.target.value)}
                  required
                >
                  {years.map((y) => {
                    const sY = new Date(y.StartYear).getFullYear();
                    const eY = new Date(y.EndYear).getFullYear();
                    return (
                      <option key={y.Fid} value={y.Fid}>
                        FY {sY}-{eY}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-ocean w-100 py-2 fs-6 d-flex align-items-center justify-content-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Signing In...
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-in-right fs-5"></i> Secure Login
                </>
              )}
            </button>
          </form>
        </div>

        <div className="card-footer text-center bg-light py-3 text-muted small border-0" style={{ borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }}>
          &copy; {new Date().getFullYear()} {firmTitle} &bull; Online Portal
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
